import {useState, useRef, useEffect} from 'react'
import {FacebookSyncedListing, FacebookProfile} from '../core/_requests'
import {toCdnUrl} from '../../../utils/cdnUrl'

// Facebook access token for profile pictures
const FB_ACCESS_TOKEN = process.env.REACT_APP_FB_ACCESS_TOKEN

interface ListingCardProps {
  listing: FacebookSyncedListing
  profiles?: FacebookProfile[]
  onView: () => void
  onEdit: () => void
  onDelete: () => void
  onMarkSold?: () => void
  onMarkAvailable?: () => void
  onViewMessages?: () => void
}

// Helper function to get condition label
function getConditionLabel(condition?: string): { label: string, color: string } {
  switch (condition?.toUpperCase()) {
    case 'NEW': return { label: 'חדש', color: '#198754' }
    case 'USED_LIKE_NEW': return { label: 'כמו חדש', color: '#20c997' }
    case 'USED_GOOD': return { label: 'מצב טוב', color: '#0d6efd' }
    case 'USED_FAIR': return { label: 'מצב סביר', color: '#ffc107' }
    default: return { label: '', color: '' }
  }
}

export function ListingCard({ listing, profiles, onView, onEdit, onDelete, onMarkSold, onMarkAvailable, onViewMessages }: ListingCardProps) {
  const [showMenu, setShowMenu] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Find the profile for this listing
  const profile = profiles?.find(p => p.id === listing.profile_id || p.facebook_id === listing.facebook_profile_id)

  // Mark the listing when its posting profile is logged out of Facebook — it
  // can't be published until the profile re-connects.
  const isProfileLoggedOut = !!profile && profile.is_logged_in === false

  // Get profile picture URL
  const getProfilePicture = () => {
    if (profile?.profile_picture) return profile.profile_picture
    const fbId = profile?.facebook_id || listing.facebook_profile_id
    if (fbId) {
      return `https://graph.facebook.com/${fbId}/picture?type=small&access_token=${FB_ACCESS_TOKEN}`
    }
    return null
  }

  const profilePicture = getProfilePicture()

  const getStatusBadge = () => {
    if (listing.is_sold) return { label: 'נמכר', color: 'dark' }
    if (!listing.is_live) return { label: 'לא פעיל', color: 'secondary' }
    if (listing.listing_is_rejected) return { label: 'נדחה', color: 'danger' }
    if (listing.is_pending) return { label: 'ממתין', color: 'warning' }
    return { label: 'פעיל', color: 'success' }
  }

  const status = getStatusBadge()
  const conditionInfo = getConditionLabel(listing.condition)
  const totalMessages = listing.messages_count || listing.conversations?.length || 0
  const pendingDelete = listing.pending_delete?.status
  const pendingEdit = listing.pending_edit?.status
  const pendingLabel = pendingDelete
    ? (pendingDelete === 'failed' ? 'מחיקה נכשלה' : pendingDelete === 'processing' ? 'מוחק...' : 'מחיקה ממתינה')
    : pendingEdit
      ? (pendingEdit === 'failed' ? 'עריכה נכשלה' : pendingEdit === 'processing' ? 'מעדכן...' : 'עריכה ממתינה')
      : null
  const pendingColor = pendingDelete ? '#dc3545' : '#7239ea'
  const pendingFailed = pendingDelete === 'failed' || pendingEdit === 'failed'
  const pendingTitle = pendingDelete
    ? (listing.pending_delete?.error || `נשמר למחיקה${listing.pending_delete?.requested_at ? ' • ' + new Date(listing.pending_delete.requested_at).toLocaleString('he-IL') : ''}`)
    : pendingEdit
      ? (listing.pending_edit?.error || `שינויים נשמרו${listing.pending_edit?.requested_at ? ' • ' + new Date(listing.pending_edit.requested_at).toLocaleString('he-IL') : ''}`)
      : ''

  return (
    <div className="mp-card" style={pendingDelete ? { opacity: 0.7 } : undefined}>
      {/* Image Section */}
      <div className="mp-card__image">
        {listing.primary_photo_url ? (
          <img src={toCdnUrl(listing.primary_photo_url)} alt={listing.title} loading="lazy" />
        ) : (
          <div className="mp-card__no-image">
            <i className="bi bi-image"></i>
          </div>
        )}

        {/* Status Badge */}
        <span className={`mp-card__status mp-card__status--${status.color}`}>
          {status.label}
        </span>

        {/* Pending Edit/Delete Badge */}
        {pendingLabel && (
          <span
            title={pendingTitle}
            style={{
              position: 'absolute',
              top: '8px',
              left: '8px',
              background: pendingFailed ? '#f1416c' : pendingColor,
              color: '#fff',
              fontSize: '10px',
              fontWeight: 600,
              padding: '3px 8px',
              borderRadius: '4px',
              boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              zIndex: 2,
            }}
          >
            <i className={`bi ${pendingDelete ? 'bi-trash' : 'bi-pencil'}`} style={{ fontSize: '10px' }}></i>
            {pendingLabel}
          </span>
        )}

        {/* Condition Badge */}
        {conditionInfo.label && (
          <span className="mp-card__condition" style={{ backgroundColor: conditionInfo.color }}>
            {conditionInfo.label}
          </span>
        )}

        {/* Profile not logged in → prominent warning badge */}
        {isProfileLoggedOut && (
          <span
            title={`${profile?.name || 'הפרופיל'} אינו מחובר לפייסבוק — לא ניתן לפרסם עד התחברות מחדש`}
            style={{
              position: 'absolute',
              bottom: '8px',
              left: '8px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              background: '#dc3545',
              color: '#fff',
              fontSize: '11px',
              fontWeight: 700,
              padding: '3px 8px',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
              zIndex: 2,
            }}
          >
            <i className="bi bi-exclamation-triangle-fill" style={{ fontSize: '11px' }}></i>
            פרופיל מנותק
          </span>
        )}

        {/* Profile Picture */}
        {profilePicture && (
          <div
            className="mp-card__profile"
            title={isProfileLoggedOut
              ? `${profile?.name || 'פרופיל'} — אינו מחובר לפייסבוק`
              : (profile?.name || 'פרופיל לא ידוע')}
            style={{
              position: 'absolute',
              bottom: '8px',
              right: '8px',
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              overflow: 'hidden',
              border: isProfileLoggedOut ? '2px solid #dc3545' : '2px solid white',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
              cursor: 'default',
              opacity: isProfileLoggedOut ? 0.65 : 1,
            }}
          >
            <img
              src={profilePicture}
              alt={profile?.name || ''}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none'
              }}
            />
          </div>
        )}
      </div>

      {/* Content Section */}
      <div className="mp-card__content">
        {/* Price - Most Important */}
        <div className="mp-card__price">
          {listing.formatted_price || `₪${listing.price?.toLocaleString()}`}
          {(listing as any).pending_edit?.changes?.price != null && Number((listing as any).pending_edit.changes.price) !== Number(listing.price) && (
            <span style={{ fontSize: '12px', fontWeight: 500, color: '#b26a00', marginRight: '6px' }}>
              (מחיר חדש ממתין: ₪{Number((listing as any).pending_edit.changes.price).toLocaleString()})
            </span>
          )}
          {listing.is_negotiable && <span className="mp-card__negotiable">גמיש</span>}
        </div>

        {/* Title */}
        <h3 className="mp-card__title">{listing.title}</h3>

        {/* Stats Row */}
        <div className="mp-card__stats">
          <span title="צפיות">
            <i className="bi bi-eye"></i>
            {listing.views_count || 0}
          </span>
          <span
            title="הודעות - לחץ לצפייה"
            className={onViewMessages ? 'mp-card__stats--clickable' : ''}
            onClick={(e) => {
              if (onViewMessages) {
                e.stopPropagation()
                onViewMessages()
              }
            }}
          >
            <i className="bi bi-chat-dots"></i>
            {totalMessages}
          </span>
          {listing.location && (
            <span title="מיקום">
              <i className="bi bi-geo-alt"></i>
              {listing.location.split(',')[0]}
            </span>
          )}
        </div>
      </div>

      {/* Actions - Single Dropdown */}
      <div className="mp-card__actions" ref={menuRef}>
        <button
          className="mp-card__menu-btn"
          onClick={() => setShowMenu(!showMenu)}
          aria-label="פעולות"
        >
          <i className="bi bi-three-dots-vertical"></i>
        </button>

        {showMenu && (
          <div className="mp-card__dropdown">
            <button onClick={() => { onView(); setShowMenu(false); }}>
              <i className="bi bi-info-circle"></i>
              <span>פרטים</span>
            </button>
            <a
              href={listing.share_uri}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setShowMenu(false)}
            >
              <i className="bi bi-facebook"></i>
              <span>צפה בפייסבוק</span>
            </a>
            <button onClick={() => { onEdit(); setShowMenu(false); }}>
              <i className="bi bi-pencil"></i>
              <span>ערוך</span>
            </button>
            {/* Mark as sold - only show if not already sold */}
            {!listing.is_sold && onMarkSold && (
              <button className="mp-card__dropdown--warning" onClick={() => { onMarkSold(); setShowMenu(false); }}>
                <i className="bi bi-tag"></i>
                <span>סמן כנמכר</span>
              </button>
            )}
            {/* Mark as available - only show if sold */}
            {listing.is_sold && onMarkAvailable && (
              <button className="mp-card__dropdown--success" onClick={() => { onMarkAvailable(); setShowMenu(false); }}>
                <i className="bi bi-arrow-counterclockwise"></i>
                <span>סמן כזמין</span>
              </button>
            )}
            <button className="mp-card__dropdown--danger" onClick={() => { onDelete(); setShowMenu(false); }}>
              <i className="bi bi-trash"></i>
              <span>מחק</span>
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
